import Button from "devextreme-react/button";
import TagBox from "devextreme-react/tag-box";
import { confirm } from "devextreme/ui/dialog";
import React, { useState, useEffect, useMemo } from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { PhaseWorkflowType } from "../../../dtos/PhaseWorkflowType";
import { WorkflowConfigOptionsDto } from "../../../dtos/WorkflowConfigOptionsDto";
import { UserRole } from "../../../dtos/UserRole";
import { PhaseStatus } from "../../../dtos/PhaseStatus";
import { PhaseAction } from "../../../dtos/PhaseAction";
import { groupRoleOptions } from "../../shared/tasklist/config/audienceConfig";
import { isStepStyleWorkflow } from "../../shared/Utils";
import AddRowsDialog from "./components/AddRowsDialog";
import TemplateGroupBox, { removedTextStyle } from "./components/TemplateGroupBox";
import useExpandedGroups from "./hooks/useExpandedGroups";
import useStagedRows from "./hooks/useStagedRows";

type Props = {
    task: DefaultPhaseDto;
    configOptions: WorkflowConfigOptionsDto;
    onSave: (data: DefaultPhaseDto) => Promise<void>;
    workflowType?: PhaseWorkflowType;
};

type ActionEntry = {
    status: PhaseStatus;
    action: PhaseAction;
    roles: UserRole[];
};

// For simplified mode (STEP workflows), we track just action -> roles
type SimplifiedActionEntry = {
    action: PhaseAction;
    roles: UserRole[];
};

export default function TemplateActionsTab({ task, configOptions, onSave, workflowType }: Props) {
    const actionOptions = configOptions.task.actions;
    const roleOptions = configOptions.roles.filter(r => !r.notificationOnly);
    const groupedRoles = useMemo(() => groupRoleOptions(roleOptions), [roleOptions]);

    // Step-style workflows (Step and Step Hybrid) have no task-level status, so the backend
    // matches their actions regardless of status. Use the simplified one-row-per-action form.
    const useSimplifiedActions = isStepStyleWorkflow(workflowType);

    // Get current actions config
    const currentActions = task.settings?.workflow?.actions?.actions ?? [];

    // Build initial state for standard mode
    const buildInitialState = (): ActionEntry[] => {
        return actionOptions.map(opt => {
            const existing = currentActions.find(
                a => a.status === opt.status && a.action === opt.action
            );
            return {
                status: opt.status,
                action: opt.action,
                roles: existing?.roles ?? []
            };
        });
    };

    // Build initial state for simplified mode - one entry per unique action
    const buildSimplifiedInitialState = (): SimplifiedActionEntry[] => {
        const uniqueActions = [...new Set(actionOptions.map(opt => opt.action))];
        return uniqueActions.map(action => {
            // Get roles from any existing entry for this action (they should all be the same)
            const existing = currentActions.find(a => a.action === action);
            return {
                action,
                roles: existing?.roles ?? []
            };
        });
    };

    const [actions, setActions] = useState<ActionEntry[]>(buildInitialState);
    const [savedActions, setSavedActions] = useState<ActionEntry[]>(buildInitialState);
    const [simplifiedActions, setSimplifiedActions] = useState<SimplifiedActionEntry[]>(buildSimplifiedInitialState);
    const [savedSimplifiedActions, setSavedSimplifiedActions] = useState<SimplifiedActionEntry[]>(buildSimplifiedInitialState);

    // Reset state when task changes
    const staged = useStagedRows();

    useEffect(() => {
        staged.clear();
        const initial = buildInitialState();
        setActions(initial);
        setSavedActions(initial);
        const simplifiedInitial = buildSimplifiedInitialState();
        setSimplifiedActions(simplifiedInitial);
        setSavedSimplifiedActions(simplifiedInitial);
    }, [task.id]);

    // What the save would write: a struck-through row keeps its roles on screen but saves as empty
    const keptActions = useMemo(
        () => actions.map(a => staged.isRemoved(`${a.status}-${a.action}`) ? { ...a, roles: [] } : a),
        [actions, staged]);

    const isDirty = useMemo(() => {
        if (useSimplifiedActions) {
            const toComparable = (actionList: SimplifiedActionEntry[]) => {
                return actionList
                    .map(a => `${a.action}-[${a.roles.slice().sort().join(",")}]`)
                    .sort()
                    .join(";");
            };
            return toComparable(simplifiedActions) !== toComparable(savedSimplifiedActions);
        } else {
            const toComparable = (actionList: ActionEntry[]) => {
                return actionList
                    .map(a => `${a.status}-${a.action}-[${a.roles.slice().sort().join(",")}]`)
                    .sort()
                    .join(";");
            };
            return toComparable(keptActions) !== toComparable(savedActions);
        }
    }, [keptActions, savedActions, simplifiedActions, savedSimplifiedActions, useSimplifiedActions]);

    const updateActionRoles = (status: PhaseStatus, action: PhaseAction, newRoles: UserRole[]) => {
        setActions(prev =>
            prev.map(a =>
                a.status === status && a.action === action
                    ? { ...a, roles: newRoles }
                    : a
            )
        );
    };

    const getRolesForAction = (status: PhaseStatus, action: PhaseAction): UserRole[] => {
        const entry = actions.find(a => a.status === status && a.action === action);
        return entry?.roles ?? [];
    };

    // Simplified mode: update roles for an action (applies to all statuses)
    const updateSimplifiedActionRoles = (action: PhaseAction, newRoles: UserRole[]) => {
        setSimplifiedActions(prev =>
            prev.map(a => a.action === action ? { ...a, roles: newRoles } : a)
        );
    };

    const getSimplifiedRolesForAction = (action: PhaseAction): UserRole[] => {
        const entry = simplifiedActions.find(a => a.action === action);
        return entry?.roles ?? [];
    };

    // Restore defaults from the backend config
    // One click empties every action, so it is worth asking first
    const handleClearAll = async () => {
        const confirmed = await confirm("Remove the roles from every action on this tab?", "Clear All");
        if (!confirmed) return;
        if (useSimplifiedActions) {
            setSimplifiedActions(prev => prev.map(a => ({ ...a, roles: [] })));
        } else {
            setActions(prev => prev.map(a => ({ ...a, roles: [] })));
        }
        staged.clear();
    };

    const handleRestoreDefaults = () => {
        const defaultConfig = configOptions.task.defaultActions;

        if (useSimplifiedActions) {
            const uniqueActions = [...new Set(actionOptions.map(opt => opt.action))];
            setSimplifiedActions(uniqueActions.map(action => {
                const existing = defaultConfig?.actions?.find(a => a.action === action);
                return {
                    action,
                    roles: existing?.roles ?? []
                };
            }));
        } else {
            setActions(actionOptions.map(opt => {
                const existing = defaultConfig?.actions?.find(
                    a => a.status === opt.status && a.action === opt.action
                );
                return {
                    status: opt.status,
                    action: opt.action,
                    roles: existing?.roles ?? []
                };
            }));
        }
    };

    const handleSave = async () => {
        let actionsToSave: ActionEntry[];

        if (useSimplifiedActions) {
            // For STEP workflows, task-level actions are status-independent
            // We only need one status to indicate "enabled" - the action check ignores status
            const firstStatus = actionOptions[0]?.status ?? PhaseStatus.NOT_STARTED;
            actionsToSave = [];
            for (const sa of simplifiedActions) {
                if (sa.roles.length > 0) {
                    // Add this action with a single status marker
                    actionsToSave.push({
                        status: firstStatus,
                        action: sa.action,
                        roles: sa.roles
                    });
                }
            }
        } else {
            // Filter to only actions with roles assigned
            actionsToSave = keptActions.filter(a => a.roles.length > 0);
        }

        const updatedTask: DefaultPhaseDto = {
            ...task,
            settings: {
                ...task.settings,
                workflow: {
                    permissions: task.settings?.workflow?.permissions ?? null,
                    actions: {
                        actions: actionsToSave
                    }
                }
            }
        };
        await onSave(updatedTask);

        if (useSimplifiedActions) {
            setSavedSimplifiedActions([...simplifiedActions]);
        } else {
            setSavedActions([...keptActions]);
            setActions([...keptActions]);
            staged.clear();
        }
    };

    const handleDiscard = () => {
        if (useSimplifiedActions) {
            setSimplifiedActions([...savedSimplifiedActions]);
        } else {
            setActions([...savedActions]);
            staged.clear();
        }
    };

    // Get unique actions for simplified mode
    const uniqueActions = useMemo(() => {
        const seen = new Set<PhaseAction>();
        return actionOptions.filter(opt => {
            if (seen.has(opt.action)) return false;
            seen.add(opt.action);
            return true;
        });
    }, [actionOptions]);

    // Group actions by action type for standard mode grouped display
    const groupedActions = useMemo(() => {
        const groups: { action: PhaseAction; options: typeof actionOptions }[] = [];
        const actionOrder: PhaseAction[] = [];

        for (const opt of actionOptions) {
            if (!actionOrder.includes(opt.action)) {
                actionOrder.push(opt.action);
                groups.push({ action: opt.action, options: [] });
            }
            const group = groups.find(g => g.action === opt.action);
            if (group) {
                group.options.push(opt);
            }
        }

        return groups;
    }, [actionOptions]);

    // Extract status label from action option label (the part in parentheses)
    const getStatusLabel = (opt: typeof actionOptions[0]): string => {
        const match = opt.label.match(/\((.+)\)$/);
        return match ? match[1] : opt.label;
    };

    // Get action label without status suffix
    const getActionLabel = (action: PhaseAction): string => {
        const opt = actionOptions.find(o => o.action === action);
        if (!opt) return String(action);
        // Remove status suffix like " (at Setup)"
        const match = opt.label.match(/^(.+?)(\s*\(.+\))$/);
        return match ? match[1] : opt.label;
    };

    const getActionDescription = (action: PhaseAction): string | undefined => {
        const opt = actionOptions.find(o => o.action === action);
        return opt?.description ?? undefined;
    };

    const groups = useExpandedGroups(groupedActions.map(g => g.action));
    const [addForAction, setAddForAction] = useState<PhaseAction | null>(null);

    const rowKey = (opt: { status: PhaseStatus; action: PhaseAction }) => `${opt.status}-${opt.action}`;

    // The action and its statuses are fixed; a status row is on screen once it has roles, or once
    // the user has added it and not picked any yet - the rule the permissions tab uses.
    const rowsFor = (options: typeof actionOptions) => options.filter(o =>
        getRolesForAction(o.status, o.action).length > 0 || staged.isAdded(rowKey(o)));

    const groupSummary = (options: typeof actionOptions) => {
        const kept = rowsFor(options).filter(o => !staged.isRemoved(rowKey(o))).length;
        if (kept === 0) return "none configured";
        return `${kept} ${kept === 1 ? "status" : "statuses"} configured`;
    };

    // Adding opens the group, so what was just added is visible
    const addRows = (action: PhaseAction, keys: string[], roles: UserRole[]) => {
        staged.addRows(keys);
        groups.expand(action);
        setActions(prev => prev.map(a => keys.includes(`${a.status}-${a.action}`) ? { ...a, roles } : a));
        setAddForAction(null);
    };

    const descriptionText = useSimplifiedActions
        ? "Configure which roles can perform each action. Actions apply regardless of task status."
        : "Configure which roles can perform each action at each status.";

    return (
        <div className="template-tab">
            <div className="template-tab-body">
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "16px" }}>
                <p style={{ flex: 1, margin: 0, color: "#666" }}>
                    {descriptionText}
                </p>
                {!useSimplifiedActions && groupedActions.length > 0 && (
                    <Button
                        icon={groups.allExpanded ? "chevronup" : "chevrondown"}
                        text={groups.allExpanded ? "Collapse All" : "Expand All"}
                        stylingMode="outlined"
                        onClick={groups.toggleAll}
                    />
                )}
            </div>

            <div style={{ display: "grid", gap: "16px" }}>
                {useSimplifiedActions ? (
                    // Simplified mode: one row per action
                    uniqueActions.map(opt => (
                        <div
                            key={opt.action}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "12px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px"
                            }}
                        >
                            <div style={{ width: "250px", minWidth: "250px" }}>
                                <div>
                                    <span style={{ fontWeight: 500 }}>{getActionLabel(opt.action)}</span>
                                </div>
                                {getActionDescription(opt.action) && (
                                    <div style={{ fontSize: "12px", color: "#888" }}>{getActionDescription(opt.action)}</div>
                                )}
                            </div>
                            <TagBox
                                items={groupedRoles}
                                displayExpr="name"
                                valueExpr="id"
                                grouped={true}
                                value={getSimplifiedRolesForAction(opt.action)}
                                onValueChanged={(e) => updateSimplifiedActionRoles(opt.action, e.value ?? [])}
                                width="100%"
                                placeholder="Select roles..."
                                showSelectionControls={true}
                            />
                        </div>
                    ))
                ) : (
                    // Standard mode: grouped by action in bordered containers
                    groupedActions.map(group => (
                        <TemplateGroupBox
                            key={group.action}
                            title={getActionLabel(group.action)}
                            summary={groupSummary(group.options)}
                            collapsed={!groups.isExpanded(group.action)}
                            onToggle={() => groups.toggle(group.action)}
                            testId="action-group"
                            toggleTestId="action-collapse-toggle"
                            headerRight={
                                <Button
                                    icon="add"
                                    text="Add Status"
                                    stylingMode="text"
                                    disabled={rowsFor(group.options).length === group.options.length}
                                    hint={rowsFor(group.options).length === group.options.length
                                        ? "Every status for this action is configured"
                                        : undefined}
                                    onClick={() => setAddForAction(group.action)}
                                />
                            }
                        >
                            <div style={{ display: "grid", gap: "8px" }}>
                                {rowsFor(group.options).length === 0 && (
                                    <p style={{ color: "#888", fontSize: "13px", margin: 0 }}>
                                        No statuses configured for this action.
                                    </p>
                                )}
                                {rowsFor(group.options).map(opt => {
                                    const removed = staged.isRemoved(rowKey(opt));
                                    return (
                                        <div
                                            key={rowKey(opt)}
                                            data-testid="action-row"
                                            data-removed={removed}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px"
                                            }}
                                        >
                                            <div style={{ width: "180px", minWidth: "180px" }}>
                                                <span style={{ fontSize: "13px", ...(removed ? removedTextStyle : { color: "#555" }) }}>
                                                    {getStatusLabel(opt)}
                                                </span>
                                            </div>
                                            <TagBox
                                                items={groupedRoles}
                                                displayExpr="name"
                                                valueExpr="id"
                                                grouped={true}
                                                value={getRolesForAction(opt.status, opt.action)}
                                                onValueChanged={(e) => updateActionRoles(opt.status, opt.action, e.value ?? [])}
                                                width="100%"
                                                placeholder="Select roles..."
                                                showSelectionControls={true}
                                                readOnly={removed}
                                            />
                                            <Button
                                                icon={removed ? "undo" : "trash"}
                                                stylingMode="text"
                                                hint={removed ? `Keep ${getStatusLabel(opt)}` : `Remove ${getStatusLabel(opt)}`}
                                                onClick={() => removed ? staged.restoreRow(rowKey(opt)) : staged.removeRow(rowKey(opt))}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </TemplateGroupBox>
                    ))
                )}
            </div>

            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save Actions *" : "Save Actions"}
                    type="default"
                    stylingMode="contained"
                    onClick={handleSave}
                    disabled={!isDirty}
                />
                {isDirty && (
                    <Button
                        text="Discard Changes"
                        type="normal"
                        stylingMode="outlined"
                        onClick={handleDiscard}
                    />
                )}
                <Button
                    text="Restore Defaults"
                    type="normal"
                    stylingMode="outlined"
                    onClick={handleRestoreDefaults}
                />
                <Button
                    text="Clear All"
                    type="danger"
                    stylingMode="outlined"
                    onClick={handleClearAll}
                />
                {isDirty && (
                    <span style={{ color: "#f0ad4e", fontSize: "12px", marginLeft: "8px" }}>
                        Unsaved changes
                    </span>
                )}
            </div>

            <AddRowsDialog
                visible={addForAction !== null}
                title={addForAction === null ? "Add Status" : `Add Status - ${getActionLabel(addForAction)}`}
                rowLabel="Statuses"
                rowItems={(groupedActions.find(g => g.action === addForAction)?.options ?? [])
                    .filter(o => !rowsFor([o]).length)
                    .map(o => ({ id: rowKey(o), name: getStatusLabel(o) }))}
                roleItems={groupedRoles}
                onCancel={() => setAddForAction(null)}
                onAdd={(keys, roles) => addRows(addForAction!, keys, roles)}
            />
        </div>
    );
}
